import React from 'react';
import { Box, Typography, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Card, CardContent, IconButton, Tooltip, Button } from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Save as SaveIcon, Close as CloseIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { getEndpoint, API_ENDPOINTS } from '../api/apiConstants';
import { api } from '../api/fetchWrapper';
import { createStepRequestValidator, updateStepRequestValidator, deleteStepRequestValidator } from '../api/features/jobDetail/jobDetailApi';
import { SAPButton } from '../components/design-system/SAPButton';
import { SAPTextField } from '../components/design-system/SAPTextField';
import { SAP_COLORS, SAP_TYPOGRAPHY, SAP_SPACING, SAP_SHADOWS, SAP_TRANSITIONS } from '../theme/sapDesignSystem';
import { useClientCode } from '../api/auth/ClientCodeContext';
import { toast } from 'sonner';

interface Props {
  stepId: number;
}

type StepRequestValidator = { id?: number; stepId?: number; requestValidation?: string; sequence?: number; resultFlag?: string };

export default function StepRequestValidator({ stepId }: Props) {
  const { clientCode } = useClientCode();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [rows, setRows] = React.useState<StepRequestValidator[]>([]);

  const [editOpen, setEditOpen] = React.useState(false);
  const [editRow, setEditRow] = React.useState<StepRequestValidator | null>(null);
  const [inlineEditingId, setInlineEditingId] = React.useState<number | null>(null);
  const [inlineSequence, setInlineSequence] = React.useState<string>('');
  const [inlineResultFlag, setInlineResultFlag] = React.useState<string>('');
  const [inlineRequestValidation, setInlineRequestValidation] = React.useState<string>('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [deleteConfirmRow, setDeleteConfirmRow] = React.useState<StepRequestValidator | null>(null);
  const [deleting, setDeleting] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!stepId) return;
    setLoading(true);
    setError(null);
    try {
      const endpoint = getEndpoint(clientCode, API_ENDPOINTS.JOB.STEP_REQUEST_VALIDATOR_LIST);
      const url = `${endpoint}?firstResult=-1&maxResult=1&stepId=${stepId}`;
      const data = await api.get<any>(url);
      const arr: any[] = Array.isArray(data) ? data : (Array.isArray((data as any)?.rows) ? (data as any).rows : []);
      const normalized: StepRequestValidator[] = arr.map((r: any) => Array.isArray(r)
        ? { id: r[0], stepId: r[1], requestValidation: r[2], sequence: r[3], resultFlag: r[4] }
        : { id: r.id, stepId: r.stepId, requestValidation: r.requestValidation, sequence: r.sequence, resultFlag: r.resultFlag }
      );
      setRows(normalized);
    } catch (e: any) {
      setError(e?.message || 'Failed to load validators');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [clientCode, stepId]);

  React.useEffect(() => { load(); }, [load]);

  const startInlineEdit = (row: StepRequestValidator) => {
    setInlineEditingId(row.id as number);
    setInlineSequence(String(row.sequence ?? ''));
    setInlineResultFlag(String(row.resultFlag ?? ''));
    setInlineRequestValidation(String(row.requestValidation ?? ''));
  };

  const cancelInlineEdit = () => {
    setInlineEditingId(null);
    setInlineSequence('');
    setInlineResultFlag('');
    setInlineRequestValidation('');
  };

  const openDeleteConfirm = (row: StepRequestValidator) => {
    setDeleteConfirmRow(row);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteConfirmRow?.id) return;
    setDeleting(true);
    try {
      await deleteStepRequestValidator({ clientCode, id: deleteConfirmRow.id });
      toast.success('Request validator deleted');
      await load();
      setDeleteConfirmOpen(false);
      setDeleteConfirmRow(null);
      if (inlineEditingId === deleteConfirmRow.id) cancelInlineEdit();
    } catch (e: any) {
      toast.error(e?.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card sx={{ borderRadius: 3 }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        {/* Header with actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          {/* Removed redundant "Step Request Validator" heading since it's already shown in tabs */}
          <Box sx={{ ml: 'auto' }}>
            <Tooltip title="Add Request Validator">
              <span>
                <IconButton 
                  onClick={() => { setEditRow({ id: 0, stepId, requestValidation: '', sequence: 1, resultFlag: '' }); setEditOpen(true); }} 
                  disabled={loading} 
                  sx={{ 
                    color: SAP_COLORS.sapBlue,
                    backgroundColor: SAP_COLORS.sapLightBlue,
                    borderRadius: SAP_SPACING.component.borderRadius,
                    boxShadow: SAP_SHADOWS.sm,
                    '&:hover': {
                      backgroundColor: SAP_COLORS.sapBlue,
                      color: SAP_COLORS.sapWhite,
                      transform: 'translateY(-2px)',
                      boxShadow: SAP_SHADOWS.md,
                    },
                    '&:disabled': {
                      backgroundColor: SAP_COLORS.sapGray2,
                      color: SAP_COLORS.sapGray4,
                      boxShadow: 'none',
                      transform: 'none',
                    },
                    transition: SAP_TRANSITIONS.fast,
                  }} 
                  size="large"
                >
                  <AddIcon sx={{ fontSize: 28 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
        
        <Box sx={{ border: `1px solid ${SAP_COLORS.sapGray2}`, borderRadius: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr auto', md: '2fr 120px 120px auto' }, alignItems: 'center', px: 2, py: 1.25, backgroundColor: SAP_COLORS.sapGray1, borderBottom: `1px solid ${SAP_COLORS.sapGray2}` }}>
            <Typography sx={{ color: SAP_COLORS.sapGray6, fontWeight: SAP_TYPOGRAPHY.fontWeight.medium }}>Request Validation</Typography>
            <Typography sx={{ color: SAP_COLORS.sapGray6, fontWeight: SAP_TYPOGRAPHY.fontWeight.medium, display: { xs: 'none', md: 'block' } }}>Sequence</Typography>
            <Typography sx={{ color: SAP_COLORS.sapGray6, fontWeight: SAP_TYPOGRAPHY.fontWeight.medium, display: { xs: 'none', md: 'block' } }}>Result Flag</Typography>
            <Typography sx={{ color: SAP_COLORS.sapGray6, fontWeight: SAP_TYPOGRAPHY.fontWeight.medium, display: { xs: 'none', md: 'block' } }}>Edit</Typography>
          </Box>

          {loading && (
            <Box sx={{ px: 2, py: 3 }}>
              <Typography variant="body2" color="text.secondary">Loading validators...</Typography>
            </Box>
          )}
          {error && (
            <Box sx={{ px: 2, py: 3 }}>
              <Typography variant="body2" color="error.main">{error}</Typography>
            </Box>
          )}
          {!loading && !error && rows.length === 0 && (
            <Box sx={{ px: 2, py: 3 }}>
              <Typography variant="body2" color="text.secondary">No validators found.</Typography>
            </Box>
          )}

          {rows.map((v, idx) => (
            <Box 
              key={v.id ?? idx} 
              sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr auto', md: '2fr 120px 120px auto' }, 
                alignItems: 'center', 
                px: 2, 
                py: 1.5, 
                borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                '&:hover': {
                  backgroundColor: SAP_COLORS.sapGray1 || '#f5f5f5'
                }
              }}
            >
              {/* Request Validation */}
              <Box>
                {inlineEditingId === v.id ? (
                  <SAPTextField 
                    multiline 
                    minRows={3} 
                    value={inlineRequestValidation} 
                    onChange={(e) => setInlineRequestValidation(e.target.value)}
                    size="sm"
                  />
                ) : (
                  <Typography 
                    sx={{ 
                      wordBreak: 'break-word',
                      fontSize: 13,
                      color: 'text.primary'
                    }}
                  >
                    {v.requestValidation || '-'}
                  </Typography>
                )}
              </Box>
              
              {/* Sequence */}
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                {inlineEditingId === v.id ? (
                  <SAPTextField 
                    type="number" 
                    value={inlineSequence} 
                    onChange={(e) => setInlineSequence(e.target.value)}
                    size="sm"
                  />
                ) : (
                  <Typography sx={{ fontSize: 13 }}>{v.sequence ?? '-'}</Typography>
                )}
              </Box>
              
              {/* Result Flag */}
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                {inlineEditingId === v.id ? (
                  <SAPTextField 
                    value={inlineResultFlag} 
                    onChange={(e) => setInlineResultFlag(e.target.value)}
                    size="sm"
                  />
                ) : (
                  <Typography sx={{ fontSize: 13 }}>{v.resultFlag ?? '-'}</Typography>
                )}
              </Box>
              
              {/* Edit Actions */}
              <Box display="flex" gap={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                {inlineEditingId === v.id ? (
                  <>
                    <Tooltip title="Save">
                      <IconButton 
                        onClick={async () => { 
                          try {
                            const validatorData = {
                              id: v.id!,
                              requestValidation: inlineRequestValidation,
                              sequence: Number(inlineSequence),
                              resultFlag: inlineResultFlag,
                              stepId: stepId,
                              clientId: 0
                            };
                            await updateStepRequestValidator({ clientCode, validatorData });
                            toast.success('Validator updated');
                            await load();
                            cancelInlineEdit();
                          } catch (e: any) {
                            toast.error(e?.message || 'Update failed');
                          }
                        }} 
                        sx={{ color: SAP_COLORS.sapGreen }}
                        size="small"
                      >
                        <SaveIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Cancel">
                      <IconButton 
                        onClick={cancelInlineEdit} 
                        sx={{ color: SAP_COLORS.sapGray5 }}
                        size="small"
                      >
                        <CloseIcon />
                      </IconButton>
                    </Tooltip>
                  </>
                ) : (
                  <>
                    <Tooltip title="Edit">
                      <IconButton 
                        onClick={() => startInlineEdit(v)} 
                        sx={{ color: SAP_COLORS.sapBlue }}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton 
                        onClick={() => openDeleteConfirm(v)} 
                        sx={{ color: SAP_COLORS.sapRed || '#d32f2f' }}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </CardContent>

      {/* Add/Edit dialog */}
      <Dialog 
        open={editOpen} 
        onClose={() => setEditOpen(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: SAP_SHADOWS.lg,
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2,
          borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
          fontWeight: SAP_TYPOGRAPHY.fontWeight.bold,
          fontSize: 20
        }}>
          {editRow?.id ? 'Edit Request Validator' : 'Add Request Validator'}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Request Validation */}
            <Box>
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 1, 
                  fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
                  color: 'text.primary',
                  fontSize: 14
                }}
              >
                Request Validation <span style={{ color: SAP_COLORS.sapRed }}>*</span>
              </Typography>
              <SAPTextField 
                multiline 
                minRows={6} 
                maxRows={10}
                value={editRow?.requestValidation || ''} 
                onChange={(e) => setEditRow(r => ({ ...(r || {}), requestValidation: e.target.value }))}
                placeholder="Enter validation logic or expression..."
                sx={{
                  '& .MuiInputBase-input': {
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                    fontSize: 13
                  }
                }}
              />
              <Typography variant="caption" sx={{ mt: 0.5, color: 'text.secondary', display: 'block' }}>
                Enter the validation expression or logic for this request validator
              </Typography>
            </Box>

            {/* Sequence and Result Flag in a row */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mb: 1, 
                    fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
                    color: 'text.primary',
                    fontSize: 14
                  }}
                >
                  Sequence <span style={{ color: SAP_COLORS.sapRed }}>*</span>
                </Typography>
                <SAPTextField 
                  type="number" 
                  value={editRow?.sequence ?? ''} 
                  onChange={(e) => setEditRow(r => ({ ...(r || {}), sequence: Number(e.target.value) }))}
                  placeholder="1"
                  inputProps={{ min: 1 }}
                />
                <Typography variant="caption" sx={{ mt: 0.5, color: 'text.secondary', display: 'block' }}>
                  Order of execution
                </Typography>
              </Box>
              <Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mb: 1, 
                    fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
                    color: 'text.primary',
                    fontSize: 14
                  }}
                >
                  Result Flag
                </Typography>
                <SAPTextField 
                  value={editRow?.resultFlag || ''} 
                  onChange={(e) => setEditRow(r => ({ ...(r || {}), resultFlag: e.target.value }))}
                  placeholder="SUCCESS"
                />
                <Typography variant="caption" sx={{ mt: 0.5, color: 'text.secondary', display: 'block' }}>
                  Expected result flag (e.g., SUCCESS, FAILURE)
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, borderTop: `1px solid ${SAP_COLORS.sapGray2}`, gap: 1 }}>
          <SAPButton 
            variant="ghost" 
            onClick={() => setEditOpen(false)}
            sx={{ minWidth: 100 }}
          >
            Cancel
          </SAPButton>
          <SAPButton 
            variant="primary" 
            icon="save" 
            onClick={async () => { 
            try {
              if (!editRow) return;
              if (!editRow.id) {
                const validatorData = {
                  requestValidation: editRow.requestValidation || '',
                  sequence: editRow.sequence || 1,
                  resultFlag: editRow.resultFlag || 'SUCCESS',
                  stepId: stepId,
                  clientId: 0
                };
                await createStepRequestValidator({ clientCode, validatorData });
                toast.success('Validator created');
              } else {
                const validatorData = {
                  id: editRow.id,
                  requestValidation: editRow.requestValidation || '',
                  sequence: editRow.sequence || 1,
                  resultFlag: editRow.resultFlag || 'SUCCESS',
                  stepId: stepId,
                  clientId: 0
                };
                await updateStepRequestValidator({ clientCode, validatorData });
                toast.success('Validator updated');
              }
              setEditOpen(false);
              await load();
            } catch (e: any) {
              toast.error(e?.message || 'Save failed');
            }
          }}>Save</SAPButton>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={deleteConfirmOpen} onClose={() => !deleting && (setDeleteConfirmOpen(false), setDeleteConfirmRow(null))} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold }}>Delete request validator?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this request validator? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setDeleteConfirmOpen(false); setDeleteConfirmRow(null); }} disabled={deleting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDelete} disabled={deleting}>{deleting ? 'Deleting…' : 'Delete'}</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}